import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { withWindow } from '@shared/utils/withWindow';
import type { MegaNavSlotId } from './megaNav.config';

/** Only one mega nav renders per page, so a fixed id is safe (React 17: no `useId`). */
export const MEGA_NAV_PANEL_ID = 'category-mega-nav-panel';

/** Hover intent: don't flash the panel on a quick pass over the bar. */
export const MEGA_NAV_OPEN_DELAY_MS = 120;
/** Grace period so the pointer can travel from a slot down into the panel. */
export const MEGA_NAV_CLOSE_DELAY_MS = 250;

type TriggerLike = Readonly<{ id: MegaNavSlotId; hasLink: boolean }>;

/**
 * Client-only open/close state for the mega nav: hover with intent delays, focus
 * opens immediately, Escape closes and returns focus, tabbing out of the nav closes,
 * and any route change closes. The server always renders the panel closed.
 */
export const useCategoryMegaNav = () => {
  const [openSlotId, setOpenSlotId] = useState<MegaNavSlotId | null>(null);
  const openTimeoutRef = useRef<number | undefined>();
  const closeTimeoutRef = useRef<number | undefined>();
  const rootRef = useRef<HTMLElement | null>(null);
  const triggerRefs = useRef<Partial<Record<MegaNavSlotId, HTMLElement | null>>>({});
  // Set right before we programmatically refocus a trigger (Escape), so that focus
  // doesn't immediately re-open the panel we just closed.
  const skipNextFocusOpenRef = useRef(false);
  const history = useHistory();

  const clearTimers = useCallback(() => {
    withWindow(global => {
      global.clearTimeout(openTimeoutRef.current);
      global.clearTimeout(closeTimeoutRef.current);
    });
  }, []);

  const openNow = useEventCallback((slotId: MegaNavSlotId) => {
    clearTimers();
    setOpenSlotId(slotId);
  });

  const closeNow = useEventCallback(() => {
    clearTimers();
    setOpenSlotId(null);
  });

  const scheduleOpen = useEventCallback((slotId: MegaNavSlotId) => {
    clearTimers();
    // Switching between slots while the panel is already open should feel instant.
    const delay = openSlotId ? 0 : MEGA_NAV_OPEN_DELAY_MS;
    withWindow(global => {
      openTimeoutRef.current = global.setTimeout(() => setOpenSlotId(slotId), delay);
    });
  });

  const scheduleClose = useEventCallback(() => {
    clearTimers();
    withWindow(global => {
      closeTimeoutRef.current = global.setTimeout(() => setOpenSlotId(null), MEGA_NAV_CLOSE_DELAY_MS);
    });
  });

  const cancelClose = useEventCallback(() => {
    withWindow(global => global.clearTimeout(closeTimeoutRef.current));
  });

  // Route change: close AND drop any pending hover-intent timer, or a 120ms open could
  // fire after navigation and pop the panel over the new page.
  useEffect(() => {
    const unregister = history.listen(closeNow);
    return () => {
      unregister();
      clearTimers();
    };
  }, [history, closeNow, clearTimers]);

  // Escape closes even when the panel was opened by hover and focus is elsewhere on
  // the page; if focus is inside the nav it returns to the open slot's trigger.
  useEffect(() => {
    if (!openSlotId) return;
    return withWindow(global => {
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'Escape') return;
        const focusIsInsideNav = !!rootRef.current?.contains(global.document.activeElement);
        closeNow();
        if (focusIsInsideNav) {
          skipNextFocusOpenRef.current = true;
          try {
            triggerRefs.current[openSlotId]?.focus();
          } finally {
            skipNextFocusOpenRef.current = false;
          }
        }
      };
      global.document.addEventListener('keydown', onKeyDown);
      return () => global.document.removeEventListener('keydown', onKeyDown);
    }, undefined);
  }, [openSlotId, closeNow]);

  const getRootProps = useCallback(
    () => ({
      ref: rootRef,
      onMouseLeave: scheduleClose,
      // Focus moved outside the nav (e.g. tabbing past the last panel link).
      onBlur: (event: React.FocusEvent) => {
        const next = event.relatedTarget as Node | null;
        if (!next || !rootRef.current?.contains(next)) closeNow();
      },
    }),
    [scheduleClose, closeNow],
  );

  const getTriggerProps = useCallback(
    (trigger: TriggerLike) => {
      const isOpen = openSlotId === trigger.id;
      return {
        ref: (element: HTMLElement | null) => {
          triggerRefs.current[trigger.id] = element;
        },
        onMouseEnter: () => scheduleOpen(trigger.id),
        onFocus: () => {
          if (skipNextFocusOpenRef.current) return;
          openNow(trigger.id);
        },
        // Grouped slots have no destination of their own, so a click (or tap) opens the
        // panel. It never closes it: hover has usually opened the panel already, so a
        // toggle would shut it on the very click that follows. Escape / leaving closes.
        onClick: trigger.hasLink ? undefined : () => openNow(trigger.id),
        'aria-expanded': isOpen,
        'aria-controls': isOpen ? MEGA_NAV_PANEL_ID : undefined,
      };
    },
    [openSlotId, scheduleOpen, openNow],
  );

  const getPanelProps = useCallback(
    () => ({
      id: MEGA_NAV_PANEL_ID,
      onMouseEnter: cancelClose,
    }),
    [cancelClose],
  );

  return { openSlotId, closeNow, getRootProps, getTriggerProps, getPanelProps };
};
